/**
 * Pattern for a "does it already have http:// or https://" check.
 */
export const PROTOCOL_PATTERN = /^https?:\/\//i;

/**
 * Pattern for ANY URI scheme prefix (RFC 3986: ALPHA *( ALPHA / DIGIT / "+"
 * / "-" / "." ) ":"), e.g. "http:", "https:", "ftp:", "mailto:", "tel:",
 * "javascript:". Used to tell a bare domain (no scheme at all) apart from a
 * value that already declares some other scheme.
 */
const ANY_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/**
 * Prepends https:// to a bare-domain URL (e.g. "historycolorado.org" ->
 * "https://historycolorado.org") so it works as a clickable link without
 * forcing the user to type the protocol themselves. Mirrors the server-side
 * `normalizeUrl` (server/src/utils/normalize-url.js).
 *
 * A value that already declares SOME scheme (http:, https:, ftp:, mailto:,
 * javascript:, ...) is left untouched -- guessing would mangle it into a
 * double-scheme string (e.g. "ftp://x" -> "https://ftp://x"). Callers that
 * need to reject an unsupported scheme should pair this with
 * `hasUnsupportedScheme`.
 *
 * @param {string} url - Raw URL value (may be undefined/null/empty).
 * @returns {string} url unchanged if empty or already has a scheme,
 *   otherwise prefixed with https://.
 */
export function normalizeUrl(url) {
    if (!url) return url;
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    return ANY_SCHEME_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * True if `url` already declares an explicit scheme that ISN'T http/https
 * (e.g. "ftp://x", "javascript:alert(1)", "mailto:a@b.com"). `normalizeUrl`
 * intentionally leaves these untouched rather than guessing, so form
 * validation should flag them explicitly instead of silently accepting or
 * mangling them.
 *
 * @param {string} url - Raw URL value (may be undefined/null/empty).
 * @returns {boolean}
 */
export function hasUnsupportedScheme(url) {
    if (!url) return false;
    const trimmed = url.trim();
    return ANY_SCHEME_PATTERN.test(trimmed) && !PROTOCOL_PATTERN.test(trimmed);
}

export default normalizeUrl;
